<div class="fap-shell" [class.fap-shell--dev]="isDeveloperPortal()">
  <!-- Skip-to-content link (UX wave 1, a11y). Visually hidden until a
       keyboard user tabs to it; jumps focus straight to <main id="content">
       so the header nav can be bypassed. -->
  <a class="fap-skip-link" href="#content">{{ 'i18n.fap.a11y.skip_to_content' | translate }}</a>
  <!-- Header: mirrors the federal ePortal pattern. Swiss Confederation
       word-mark + app title on the left, user controls on the right.
       The /developer/* routes swap admin controls (persona switcher,
       task inbox) for a lean dev-portal header. -->
  <header class="fap-header" role="banner">
    <!-- 0.62.x — Logos konfigurierbar via environment.branding. Default
         im Pilot ist "Schweizland · Pilot" (fiktives Land), damit klar
         ist dass die Pilot-Instanz nicht offiziell ist. Production-
         Override via angular.json-fileReplacement. -->
    <div class="fap-header__logo">
      <!-- 0.62.1.33 — Flag-Logo via BrandingService.effectiveFlagSrc()
           mit Env-Label-Overlay (PROD=kein Overlay, DEV/UAT/TEST=Schraegbanner). -->
      <span class="fap-header__flag-wrap"
            [class.fap-header__flag-wrap--DEV]="showEnvOverlay() && effectiveEnvLabel() === 'DEV'"
            [class.fap-header__flag-wrap--UAT]="showEnvOverlay() && effectiveEnvLabel() === 'UAT'"
            [class.fap-header__flag-wrap--TEST]="showEnvOverlay() && effectiveEnvLabel() === 'TEST'"
            [attr.data-env-label]="showEnvOverlay() ? effectiveEnvLabel() : null">
        @if (effectiveFlagSrc()) {
          <img [src]="effectiveFlagSrc()" alt="" aria-hidden="true" />
        }
      </span>
      <!-- 0.62.1.6 — Logo-Override (URL/Base64 via BrandingService) hat
           Vorrang vor environment.branding.logoName. Nur rendern wenn gesetzt. -->
      @if (effectiveLogoSrc()) {
        <img [src]="effectiveLogoSrc()" [alt]="branding.logoNameAlt" class="fap-header__logo-name" />
      }
    </div>
    <!-- U13 task 4 (S20): the "Application" label sat on this plain div,
         where aria-label is ignored outright. role="group" makes the div
         take one; the name is the translated key, never a literal. -->
    <div class="fap-header__app" role="group"
         [attr.aria-label]="'i18n.fap.header.application' | translate">
      <!-- 0.62.1.34 — App-Brand-Name via BrandingService (PO 2026-05-22:
           statt hardcoded "FATIP" Override-baren App-Namen). Default = "FATIP". -->
      <span>{{ effectiveAppName() }}</span>
      @if (appVersion()) {
        <a class="fap-header__version" routerLink="/modules/roadmap"
           [attr.title]="'i18n.fap.header.version_tooltip' | translate">
          v{{ appVersion() }}
        </a>
      }
      <!-- 0.57.60 ADR-040 — "Warum? Jetzt!"-Hook für Stakeholder-Walkthrough.
           Der Button steht direkt neben der Versions-Chip damit er auf jeder
           Page sichtbar ist; klick öffnet die Business-Case + Vision-Seite.
           BUG-008 #13 — jetzt platform-konfigurierbar: nur sichtbar wenn
           GET /v1/content/spotlight enabled=true liefert; Label = der
           admin-konfigurierte spotlight.title (SDR-014: title ist NICHT
           backend-sanitisiert → NUR Text-Interpolation, niemals [innerHTML]).
           0.62.93 (BUG-008 #13 #2) — Route /why → /spotlight umbenannt;
           /why bleibt als redirectTo-Alias erhalten (app.routes.ts). -->
      @if (spotlightEnabled()) {
        <a class="fap-header__why" routerLink="/spotlight"
           [attr.title]="'i18n.fap.header.why_now_tooltip' | translate">
          <fap-icon name="lightning" [size]="14"></fap-icon> {{ spotlightTitle() }}
        </a>
      }
      @if (isDeveloperPortal()) {
        <span class="fap-header__pill">Developer Portal</span>
      }
    </div>

    <div class="fap-header__spacer"></div>

    <!-- 0.62.0 / Pfad C.1 — Service-Navigation nach Oblique-Pattern
         (https://oblique.bit.admin.ch/guidelines/service-navigation).
         Zwei Modi:
           - eingeloggt → existierende Controls (inbox, my-account, persona)
           - anonym     → Hilfe + Anmelden (Break-Glass für jetzt; IdP-OIDC
                          kommt in Pfad C.3-C.5)
         Sprach-Switcher ist immer sichtbar (rechtsbündig). -->
    <!-- U13 task 4 (S20): the nav takes a label; the literal was untranslated
         English. The name comes from the translated key, in all four locales. -->
    <nav class="fap-service-nav" [attr.aria-label]="'i18n.fap.header.service_nav' | translate">
      @if (!isDeveloperPortal()) {
        <!-- Inbox + Mein-Konto: sobald irgendeine Session vorhanden ist
             (OIDC oder BG oder Dev-Persona). -->
        @if (hasSession()) {
          <fap-task-inbox></fap-task-inbox>
          <a class="fap-service-nav__item" routerLink="/modules/my-account"
             [attr.title]="'i18n.fap.header.my_account' | translate">
            <fap-icon name="person" [size]="18"></fap-icon>
            <span class="fap-service-nav__label">{{ 'i18n.fap.header.my_account' | translate }}</span>
          </a>
        }

        <!-- 0.62.0.1: Persona-Switcher entfernt (UI, Step 1/3). FakeAuth-
             Service + Interceptor folgen in 0.62.0.2, BE-Build-Tag-Removal
             in 0.62.0.3. OIDC ist seit 0.61.6 primary auth, kein dev-mode-
             Override mehr noetig. -->

        <!-- 0.62.x ADR-088 — Voucher-Erstellen-Button. Sichtbar nur
             für eingeloggte User (hasSession). Pilot-Onboarding für
             externe Stakeholder ohne Plattform-Account. -->
        @if (hasSession()) {
          <button type="button" class="fap-service-nav__item fap-service-nav__item--voucher"
                  (click)="openVoucherModal()"
                  [attr.title]="'i18n.fap.voucher.header_button_tooltip' | translate">
            <fap-icon name="ticket" [size]="18"></fap-icon>
            <span class="fap-service-nav__label">{{ 'i18n.fap.voucher.header_button' | translate }}</span>
          </button>
        }

        <!-- Help-Icon: immer sichtbar (auch eingeloggt, schadet nicht).
             0.72.0: zeigt neu auf den Hilfe-Bereich statt auf den
             Integrations-Leitfaden (der bleibt eigenes Modul, jetzt aus
             dem Thema "Erste Schritte" heraus verlinkt). -->
        <a class="fap-service-nav__item fap-service-nav__item--icon-only"
           routerLink="/modules/help"
           [attr.title]="'i18n.fap.header.help' | translate"
           [attr.aria-label]="'i18n.fap.header.help' | translate">
          <fap-icon name="question_circle" [size]="20" [ariaLabel]="'i18n.fap.header.help' | translate"></fap-icon>
        </a>

        <!-- Login-CTA: direkter Pfad zum IdP-OIDC-Login (FED-Login vNEXT).
             User-Wunsch: KEIN Umweg über das /login-Switchboard mit
             drei Optionen — das ist nur für Operations-Pfade (Bootstrap,
             Break-Glass) und bleibt unter /login als Fallback erreichbar.
             href="/login" als a11y/JS-disabled-Fallback. -->
        @if (showLoginCta()) {
          <a class="fap-service-nav__item fap-service-nav__item--cta" href="/login"
             (click)="startLogin($event)"
             [attr.title]="'i18n.fap.header.signin_tooltip' | translate">
            <fap-icon name="login" [size]="18"></fap-icon>
            <span class="fap-service-nav__label">{{ 'i18n.fap.header.signin' | translate }}</span>
          </a>
        }

        <!-- User-Avatar + Logout-Dropdown: sichtbar wenn OIDC-Session ODER
             Break-Glass-Session aktiv. 0.61.15 — BG-User bekommt eigenen
             Logout-Eintrag (cleart die BG-Session lokal). PO-Befund
             2026-05-03: 'owner@fatip.local hat keine Möglichkeit zum
             ausloggen' war Bug weil Header-Avatar nur für oidcAuthenticated
             sichtbar war. Klick auf Avatar öffnet Popover mit Email +
             Abmelden-Button. -->
        @if (oidcAuthenticated() || bg.isActive()) {
          <!-- U13 task 4 (S20): the "User menu" label sat on this plain div,
               where aria-label is ignored outright. It moved onto the trigger
               button (the landmark child that takes one), which is icon-only
               and had no accessible name of its own. -->
          <div class="fap-header__user">
            <!-- U13 task 5 (S21): the trigger names the list it controls.
                 Only set while the menu stands, so the closed state carries
                 no dangling IDREF. -->
            <button type="button" class="user-trigger"
                    [attr.aria-label]="'i18n.fap.header.user_menu' | translate"
                    [class.open]="userMenuOpen()"
                    (click)="toggleUserMenu($event)"
                    [attr.aria-expanded]="userMenuOpen()"
                    aria-haspopup="menu"
                    [attr.aria-controls]="userMenuOpen() ? 'fap-user-menu' : null"
                    [attr.title]="userEmail()">
              @if (userPrefs.effectiveProfileImage(); as imgSrc) {
                <img class="user-avatar user-avatar--image" [src]="imgSrc" alt="" aria-hidden="true" />
              } @else {
                <span class="user-avatar" aria-hidden="true">{{ userInitials() }}</span>
              }
              <span class="user-trigger__caret" aria-hidden="true">{{ userMenuOpen() ? '▴' : '▾' }}</span>
            </button>
            @if (userMenuOpen()) {
              <!-- U13 task 5 (S21): role="menu" owns its menuitems only when
                   nothing but role="none" stands between the two, so every
                   wrapping <li> carries role="none" (the former
                   role="presentation" elements included — role="none" is the
                   ARIA 1.2 spelling of the same statement). -->
              <ul class="user-menu" role="menu" id="fap-user-menu" (click)="$event.stopPropagation()">
                <li class="user-menu__email" role="none">{{ userEmail() }}</li>
                <li role="none"><hr class="user-menu__sep" /></li>

                <!-- 0.62.1.17 — Profilbild-Setting (FE-only Pilot, localStorage). -->
                <li role="none">
                  <button type="button" role="menuitem" class="user-menu__item"
                          (click)="toggleProfileImageEdit($event)">
                    <fap-icon name="person" [size]="16"></fap-icon>
                    <span class="user-menu__item-stack">
                      <strong>{{ 'i18n.fap.user_prefs.profile_image_label' | translate }}</strong>
                      <small>{{ 'i18n.fap.user_prefs.profile_image_hint' | translate }}</small>
                    </span>
                  </button>
                </li>
                @if (profileImageEditOpen()) {
                  <li class="user-menu__edit" role="none">
                    <label class="user-menu__edit-label">
                      {{ 'i18n.fap.user_prefs.profile_image_url' | translate }}
                      <input type="url"
                             [ngModel]="profileImageUrlInput()"
                             (ngModelChange)="profileImageUrlInput.set($event)"
                             [placeholder]="'i18n.fap.user_prefs.profile_image_url_placeholder' | translate" />
                    </label>
                    <div class="user-menu__edit-row">
                      <button type="button" class="fap-btn fap-btn--small"
                              [disabled]="!profileImageUrlInput().trim()"
                              (click)="applyProfileImageUrl()">
                        {{ 'i18n.fap.user_prefs.apply' | translate }}
                      </button>
                      <label class="user-menu__edit-file">
                        <input type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml"
                               (change)="onProfileImageFileSelected($event)" />
                        {{ 'i18n.fap.user_prefs.profile_image_upload' | translate }}
                      </label>
                    </div>
                    @if (userPrefs.effectiveProfileImage()) {
                      <button type="button" class="fap-btn fap-btn--small fap-btn--danger"
                              (click)="resetProfileImage()">
                        {{ 'i18n.fap.user_prefs.profile_image_reset' | translate }}
                      </button>
                    }
                    @if (profileImageError(); as err) {
                      <p class="user-menu__edit-error">{{ err | translate }}</p>
                    }
                    <p class="user-menu__edit-hint muted">
                      {{ 'i18n.fap.user_prefs.profile_image_pilot_hint' | translate }}
                    </p>
                  </li>
                }
                <li role="none"><hr class="user-menu__sep" /></li>
                @if (oidcAuthenticated()) {
                  <li role="none">
                    <button type="button" role="menuitem" class="user-menu__item"
                            (click)="logoutFromApp()"
                            [attr.title]="'i18n.fap.header.signout_app_tooltip' | translate">
                      <fap-icon name="logout" [size]="16"></fap-icon>
                      <span class="user-menu__item-stack">
                        <strong>{{ 'i18n.fap.header.signout_app' | translate }}</strong>
                        <small>{{ 'i18n.fap.header.signout_app_hint' | translate }}</small>
                      </span>
                    </button>
                  </li>
                  <li role="none">
                    <button type="button" role="menuitem" class="user-menu__item user-menu__item--strong"
                            (click)="logoutFromIdp()"
                            [attr.title]="'i18n.fap.header.signout_idp_tooltip' | translate">
                      <fap-icon name="logout" [size]="16"></fap-icon>
                      <span class="user-menu__item-stack">
                        <strong>{{ 'i18n.fap.header.signout_idp' | translate }}</strong>
                        <small>{{ 'i18n.fap.header.signout_idp_hint' | translate }}</small>
                      </span>
                    </button>
                  </li>
                } @else {
                  <!-- 0.61.15 — Break-Glass-Logout. Cleart nur die lokale
                       BG-Session (kein Server-Logout-Pfad nötig — Token ist
                       bg_… und vom Backend zeitlich gebunden via expiresAt). -->
                  <li role="none">
                    <button type="button" role="menuitem" class="user-menu__item user-menu__item--strong"
                            (click)="logoutBreakGlass()"
                            [attr.title]="'i18n.fap.header.signout_bg_tooltip' | translate">
                      <fap-icon name="logout" [size]="16"></fap-icon>
                      <span class="user-menu__item-stack">
                        <strong>{{ 'i18n.fap.header.signout_bg' | translate }}</strong>
                        <small>{{ 'i18n.fap.header.signout_bg_hint' | translate }}</small>
                      </span>
                    </button>
                  </li>
                }
              </ul>
            }
          </div>
        }
      } @else {
        <a class="fap-service-nav__item" routerLink="/modules">{{ 'i18n.fap.developer.admin_link' | translate }} →</a>
      }

    <!-- 0.57.65 W-16 — Kompakter Sprach-Dropdown statt 4-Button-Bar.
         Oblique-Pattern: nur die aktive Sprache als Trigger, Klick öffnet
         ein Popover mit den anderen 3. Schließt automatisch beim Klick
         außerhalb (window-listener via @HostListener im Component). -->
    <!-- U13 task 4 (S20): the label sat on this plain div, where aria-label
         is ignored outright; on the trigger it would override the visible
         "DE" and break Label-in-Name (WCAG 2.5.3). role="group" makes the
         div take one, and the trigger's name stays its visible text. -->
    <div class="fap-header__lang" role="group"
         [attr.aria-label]="'i18n.fap.header.language' | translate">
      <!-- U13 task 5 (S21): the trigger names the list it controls. Only
           set while the menu stands, so the closed state carries no dangling
           IDREF. -->
      <button type="button" class="lang-trigger"
              [class.open]="langMenuOpen()"
              (click)="toggleLangMenu($event)"
              [attr.aria-expanded]="langMenuOpen()"
              aria-haspopup="listbox"
              [attr.aria-controls]="langMenuOpen() ? 'fap-lang-menu' : null">
        {{ currentLang() | uppercase }}
        <span class="lang-trigger__caret" aria-hidden="true">{{ langMenuOpen() ? '▴' : '▾' }}</span>
      </button>
      @if (langMenuOpen()) {
        <!-- U13 task 5 (S21): role="listbox" owns its options only when
             nothing but role="none" stands between the two, so the wrapping
             <li> around each option carries role="none". -->
        <ul class="lang-menu" role="listbox" id="fap-lang-menu" (click)="$event.stopPropagation()">
          @for (l of languages; track l) {
            <li role="none">
              <button type="button" role="option"
                      [class.active]="currentLang() === l"
                      [attr.aria-selected]="currentLang() === l"
                      (click)="switchLang(l); langMenuOpen.set(false)">
                {{ l | uppercase }}
              </button>
            </li>
          }
        </ul>
      }
    </div>
    </nav>
  </header>

  <main class="fap-main" id="content">
    <router-outlet></router-outlet>
  </main>

  <!-- 0.62.x ADR-088 — Voucher-Create-Modal Mount-Point. Conditional
       gemounted (kein performance-cost wenn closed). -->
  @if (voucherModalOpen()) {
    <fap-voucher-create-modal (closed)="closeVoucherModal()"></fap-voucher-create-modal>
  }

  <!-- 0.62.x — Footer-Links + Copyright konfigurierbar via
       environment.branding. Leerer Link-String → Eintrag wird nicht
       gerendert. Seit 0.72.65 ist das der Normalfall statt einer
       Pilot-Bequemlichkeit: alle vier Standard-Legal-Links sind im Build leer.
       Copyright-Text setzt sich zusammen aus copyrightHolder + Suffix
       („Schweizland · Pilot" im Default; Production zeigt z.B.
       „Schweizerische Eidgenossenschaft" mit eigenem Pilot-Suffix). -->
  <footer class="fap-footer" role="contentinfo">
    <!-- 0.62.1.33 — Standard-Legal-Links via BrandingService.effectiveLegalLink()
         mit Override-Support (Platform-Branding-Tab).
         0.72.65: isValidLinkTarget entscheidet, ob ein Link rendert, und
         laesst http(s)-URLs und absolute Pfade durch. Spiegelt den
         isValidHttpUrl-Guard der Custom-Links weiter unten, und zwar auch in
         der Form: die Truthiness steht NICHT zusaetzlich davor, weil
         isValidLinkTarget fuer '', undefined und null bereits false liefert
         (branding.service.ts, erste Zeile der Methode). -->
    @if (isValidLinkTarget(effectiveLegalLink('imprint'))) {
      <a [href]="effectiveLegalLink('imprint')">{{ 'i18n.fap.footer.imprint' | translate }}</a>
    }
    @if (isValidLinkTarget(effectiveLegalLink('privacy'))) {
      <a [href]="effectiveLegalLink('privacy')">{{ 'i18n.fap.footer.privacy' | translate }}</a>
    }
    @if (isValidLinkTarget(effectiveLegalLink('accessibility'))) {
      <a [href]="effectiveLegalLink('accessibility')">{{ 'i18n.fap.footer.accessibility' | translate }}</a>
    }
    @if (isValidLinkTarget(effectiveLegalLink('legal'))) {
      <a [href]="effectiveLegalLink('legal')">{{ 'i18n.fap.footer.legal' | translate }}</a>
    }
    <!-- 0.62.1.6 — Plattform-Admin-konfigurierbare Custom-Footer-Links
         (BrandingService). Pro Link: URL + 4 Sprach-Labels mit DE-Fallback. -->
    @for (link of customFooterLinks(); track link.id) {
      @if (isValidHttpUrl(link.url)) {
        <a [href]="link.url" target="_blank" rel="noopener noreferrer">{{ footerLabel(link) }}</a>
      }
    }
    <span class="fap-header__spacer"></span>
    <!-- 0.62.1.33 — Footer-Title als Free-String via BrandingService
         (Default-Fallback: "© {copyrightHolder} · {pilotSuffix}").
         Developer-Portal-Suffix bleibt route-abhaengig (kein Override). -->
    <span>
      {{ effectiveFooterTitle() }}@if (isDeveloperPortal()) { · FATIP Developer Portal}
    </span>
  </footer>
</div>
